import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Badge, Dropdown, ButtonGroup, FormLabel, Button } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import PageTitle from '../../../layouts/PageTitle';
import CompanyApiService from '../../../../services/superadmin/CompanyApiService';
import GridCustomBase from '../../../components/GridCustom/GridCustomBase';
import Common from '../../../../constants/Common';
import FilterAccordion from '../../../components/Common/Filter/FilterAccordion';
import InputText from '../../../components/Forms/Element/InputText';
import ModalPage from '../../../components/Common/Modal/ModalPage';
import { errorAlert, successAlert, warnAlert, warnAlertYesOrNo } from '../../../components/alert/defaultAlert';
import { formatCNPJ } from '../../../../utils/FormatUtils';
import './CompanyApiList.css';

const CredentialField = ({ label, value, t }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
    };

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <FormLabel className="mb-0 fw-semibold">{label}</FormLabel>
                <Button
                    type="button"
                    variant={copied ? 'success' : 'light'}
                    onClick={handleCopy}
                    className="btn-sm company-api-credentials__copy"
                >
                    <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} />
                    <span className="ms-2">{copied ? t('copy.copied') : t('copy.copy')}</span>
                </Button>
            </div>

            <div className="form-control font-monospace company-api-credentials__value" style={{ minHeight: '52px', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                {value}
            </div>
        </div>
    );
};

const CompanyApiList = () => {
    const { t } = useTranslation();
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [showLoading, setShowLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [generatedCredentials, setGeneratedCredentials] = useState(null);

    // Filtros
    const [companyName, setCompanyName] = useState('');
    const [cnpj, setCnpj] = useState('');

    const toggleRow = (rowId) => {
        const normalizedId = String(rowId);
        setSelectedRows((current) => current.includes(normalizedId)
            ? current.filter((id) => id !== normalizedId)
            : [...current, normalizedId]);
    };

    const toggleAllRows = (checked) => {
        const currentPageIds = rows.map((row) => String(row.id));
        setSelectedRows(checked ? currentPageIds : []);
    };

    const search = useCallback((targetPage = page, targetSize = size) => {
        const filters = [];

        if (companyName) {
            filters.push({
                field: "name",
                value: companyName,
                operation: "MATCH"
            });
        }

        if (cnpj) {
            filters.push({
                field: "cnpj",
                value: cnpj,
                operation: "MATCH"
            });
        }

        const payload = {
            filters,
            page: targetPage,
            size: targetSize,
            order: []
        };

        // A seleção pertence à página/resultado exibido. Limpar antes da nova busca
        // evita que o estado da seleção anterior seja reaproveitado por checkboxes
        // reutilizados pelo React na atualização da tabela.
        setSelectedRows([]);
        setShowLoading(true);
        CompanyApiService.pageable(payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [companyName, cnpj, page, size]);

    const handleSearchSubmit = useCallback((e) => {
        e?.preventDefault();
        if (page !== 0) {
            setPage(0);
            return;
        }
        search(0, size);
    }, [search, page, size]);

    const handleClearFilters = useCallback(() => {
        setCompanyName('');
        setCnpj('');
        if (page !== 0) {
            setPage(0);
            return;
        }
        setTimeout(() => search(0, size), 0);
    }, [page, search, size]);

    useEffect(() => {
        search();
    }, [page, size]);

    const handleEnable = async () => {
        if (selectedRows.length !== 1) {
            warnAlert(t('company_api.select_required'));
            return;
        }
        // A chave é sempre de uma empresa só. A linha é guardada antes da busca que
        // recarrega a tabela, para o modal continuar dizendo de quem é a credencial.
        const company = rows.find((row) => String(row.id) === selectedRows[0]);

        // Ativar de novo revoga as credenciais anteriores da empresa (ver
        // CompanyApiService.enableForCompany no backend), então a confirmação avisa
        // quando já existe uma chave liberada em uso.
        const confirmText = company?.apiEnabled
            ? t('company_api.confirm_regenerate_company', { company: company.name })
            : t('company_api.confirm_activate_company', { company: company?.name });

        warnAlertYesOrNo(confirmText, async () => {
            try {
                const response = await CompanyApiService.enable(selectedRows[0]);
                setGeneratedCredentials({
                    ...response.data,
                    companyName: company?.name,
                    companyCnpj: company?.cnpj
                });
                setShowCredentialsModal(true);
                successAlert(t('company_api.activate_success'));
                setSelectedRows([]);
                search();
            } catch (error) {
                errorAlert(t('company_api.activate_error'));
            }
        });
    };

    const handleDisable = async () => {
        if (selectedRows.length === 0) {
            warnAlert(t('company_api.select_required'));
            return;
        }

        warnAlertYesOrNo(t('company_api.confirm_deactivate'), async () => {
            try {
                await Promise.all(selectedRows.map(id => CompanyApiService.disable(id)));
                successAlert(t('company_api.deactivate_success'));
                setSelectedRows([]);
                search();
            } catch (error) {
                errorAlert(t('company_api.deactivate_error'));
            }
        });
    };

    return (
        <>
            <ModalPage
                title={t('company_api.created_title')}
                show={showCredentialsModal}
                setShow={setShowCredentialsModal}
                size="lg"
                centered
                dialogClassName="company-api-credentials-modal"
                child={
                    generatedCredentials ? (
                        <div className="company-api-credentials">
                            {/* Identifica a empresa dona da chave: o secret só aparece uma vez,
                                e quem copia precisa saber para qual cliente vai entregar. */}
                            {generatedCredentials.companyName && (
                                <div className="company-api-credentials__company">
                                    <i className="fa-solid fa-building company-api-credentials__company-icon" />
                                    <div className="company-api-credentials__company-info">
                                        <span className="company-api-credentials__company-label">{t('company')}</span>
                                        <strong className="company-api-credentials__company-name">{generatedCredentials.companyName}</strong>
                                        {generatedCredentials.companyCnpj && (
                                            <span className="company-api-credentials__company-cnpj">
                                                {t('cnpj')} {formatCNPJ(generatedCredentials.companyCnpj)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <p className="mb-3 company-api-credentials__subtitle">{t('company_api.created_subtitle')}</p>

                            <CredentialField
                                label={t('company_api.client_id')}
                                value={generatedCredentials.clientId}
                                t={t}
                            />

                            <CredentialField
                                label={t('company_api.client_secret')}
                                value={generatedCredentials.clientSecret}
                                t={t}
                            />

                            <div className="company-api-credentials__warning" role="alert">
                                <div className="fw-semibold mb-1">{t('warning')}</div>
                                <div>{t('company_api.secret_one_time_warning')}</div>
                            </div>
                        </div>
                    ) : <></>
                }
            />

            <PageTitle activeMenu={t('company_api.credentials')} motherMenu={t('administration')} />

            <FilterAccordion
                onSearch={handleSearchSubmit}
                onClear={handleClearFilters}
                showQuickDates={false}
            >
                <Row className="mt-3">
                    <Col md={6}>
                        <FormLabel>{t('name')}</FormLabel>
                        <InputText
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                        />
                    </Col>
                    <Col md={6}>
                        <FormLabel>CNPJ</FormLabel>
                        <InputText
                            value={cnpj}
                            onChange={(e) => setCnpj(e.target.value)}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Row className="row mb-3 mt-4">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={handleEnable}>
                                    <i className="fa-solid fa-check"></i> {t('company_api.selected_activate')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleDisable}>
                                    <i className="fa-solid fa-ban"></i> {t('company_api.selected_deactivate')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>

                <table className="table table-list i-table style-1 mb-0 border-0">
                    <thead>
                        <tr>
                            <th className="bg-none sorting_asc" >
                                <div className="form-check style-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={rows.length > 0 && rows.every((row) => selectedRows.includes(String(row.id)))}
                                        onChange={(event) => toggleAllRows(event.target.checked)}
                                    />
                                </div>
                            </th>
                            <th>{t('name')}</th>
                            <th>CNPJ</th>
                            <th>{t('company_api.api_access')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id} className="align-middle h6">
                                <td className='item-history'>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            value={row.id}
                                            checked={selectedRows.includes(String(row.id))}
                                            onChange={() => toggleRow(row.id)}
                                        />
                                    </div>
                                </td>
                                <td>{row.name}</td>
                                <td>{row.cnpj ? formatCNPJ(row.cnpj) : '-'}</td>
                                <td>
                                    <Badge bg={row.apiEnabled ? 'success' : 'danger'} className="badge-lg">
                                        <i className={`fa ${row.apiEnabled ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
                                        {row.apiEnabled ? t('company_api.api_enabled') : t('company_api.api_disabled')}
                                    </Badge>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
};

export default CompanyApiList;
